import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { MessageLog } from "@common/models/message-log";
import { MessageLogStatusBadge } from "./MessageLogStatusBadge";
import moment from "moment";
import { formatDelta } from "@common/utils/time-format-utils";
import { Separator } from "@/components/ui/separator";
import { Copy, AlertCircle, ChevronDown, ChevronUp, WifiOff, QrCode, Wifi, Radio, RefreshCw, Send, AlertTriangle } from "lucide-react";

// ... [Keep existing code]

function GranularStatusIcons({ metadata }: { metadata: any }) {
    if (!metadata) return null;
    
    // Only show if at least one of the granular fields is present
    if (!metadata.waState && !metadata.waStream && metadata.waHasSynced === undefined) return null;

    return (
        <div className="flex items-center gap-1.5 ml-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full px-2 py-0.5">
            {/* State Icon */}
            {metadata.waState && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Wifi className={cn("h-3 w-3", metadata.waState === 'CONNECTED' ? "text-green-600 dark:text-green-400" : "text-yellow-600 dark:text-yellow-400")} />
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>State: {metadata.waState}</p>
                    </TooltipContent>
                </Tooltip>
            )}
            
            {/* Stream Icon */}
            {metadata.waStream && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Radio className={cn("h-3 w-3", metadata.waStream === 'CONNECTED' ? "text-green-600 dark:text-green-400" : "text-yellow-600 dark:text-yellow-400")} />
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>Stream: {metadata.waStream}</p>
                    </TooltipContent>
                </Tooltip>
            )}

            {/* Sync Icon */}
            {metadata.waHasSynced !== undefined && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <RefreshCw className={cn("h-3 w-3", metadata.waHasSynced ? "text-blue-600 dark:text-blue-400" : "text-yellow-600 dark:text-yellow-400")} />
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>Synced: {metadata.waHasSynced ? 'True' : 'False'}</p>
                    </TooltipContent>
                </Tooltip>
            )}
        </div>
    );
}


import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

interface SendInfo {
    sendNumber: number;
    totalSends: number;
    isDuplicate: boolean;
}

interface TimelineItemProps {
    log: MessageLog;
    isLast?: boolean;
    isFirst?: boolean;  // First item in a group (timeline line starts here)
    dueDate?: string | Date;
    previousLogTimestamp?: string | Date; // Timestamp of the previous log for delta calculation
    sendInfo?: SendInfo; // Info about this being a successful send (with send number)
}

function formatDuration(fromDate: string | Date | undefined, toDate: string | Date): string {
    if (!fromDate) return '';
    
    const from = moment(fromDate);
    const to = moment(toDate);
    const diff = to.diff(from);
    
    if (Math.abs(diff) < 1000) return 'at due time';
    
    const duration = moment.duration(Math.abs(diff));
    const isAfter = diff > 0;
    
    const parts: string[] = [];
    
    const days = Math.floor(duration.asDays());
    if (days > 0) {
        parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    }
    
    const hours = duration.hours();
    if (hours > 0 && days < 7) {
        parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    }
    
    const minutes = duration.minutes();
    if (minutes > 0 && days === 0 && hours < 24) {
        parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
    }
    
    const seconds = duration.seconds();
    if (seconds > 0 && days === 0 && hours === 0 && minutes < 5) {
        parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);
    }
    
    if (parts.length === 0) {
        return isAfter ? 'just after due time' : 'just before due time';
    }
    
    const durationStr = parts.join(' ');
    return isAfter ? `${durationStr} after due time` : `${durationStr} before due time`;
}

// formatDelta is now imported from @common/utils/time-format-utils

function WhatsAppStatusBadge({ status, waVersion }: { status: string; waVersion?: string }) {
    const statusLower = status.toLowerCase();
    
    if (statusLower === 'connected') {
        return (
            <Badge variant="outline" className={cn("text-xs px-2 py-0.5 whitespace-nowrap", 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700')}>
                <WhatsAppIcon sx={{ fontSize: 13 }} />
                <span>Connected {waVersion ? `(v${waVersion})` : ''}</span>
            </Badge>
        );
    }
    
    let config: { icon: React.ComponentType<{ className?: string }>; label: string; className: string };
    
    if (statusLower === 'disconnected') {
        config = { icon: WifiOff, label: 'Disconnected', className: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 border-red-300 dark:border-red-700' };
    } else if (statusLower === 'qrcode') {
        config = { icon: QrCode, label: 'QR Code', className: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700' };
    } else {
        config = { icon: AlertCircle, label: 'Unknown', className: 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 border-slate-300 dark:border-slate-600' };
    }
    
    const Icon = config.icon;
    
    return (
        <Badge variant="outline" className={cn("text-xs px-2 py-0.5 whitespace-nowrap", config.className)}>
            <Icon className="h-3 w-3 mr-1" />
            <span>{config.label} {statusLower === 'unknown' && waVersion ? `(v${waVersion})` : ''}</span>
        </Badge>
    );
}

export function TimelineItem({ log, isLast = false, isFirst = false, dueDate, previousLogTimestamp, sendInfo }: TimelineItemProps) {
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
    
    const durationText = dueDate && log.createdAt ? formatDuration(dueDate, log.createdAt) : null;
    const deltaText = previousLogTimestamp && log.createdAt ? formatDelta(previousLogTimestamp, log.createdAt) : null;
    
    // Debug log to check metadata
    console.log('[TimelineItem] Log metadata:', log.metadata);
    
    const retryCount = log.message?.retryCount ?? 0;
    const hasError = !!log.error;
    const errorMessage = hasError 
        ? (typeof log.error === 'string' ? log.error : log.error?.message || JSON.stringify(log.error))
        : null;
    
    // Don't show retry count for logs that indicate a retry is being triggered/initiated
    // At that point, the retry count hasn't been incremented yet and isn't relevant
    const logMessage = log.metadata?.logMessage || '';
    const isRetryTrigger = logMessage.includes('Retry triggered') || logMessage.includes('Retry initiated');
    const shouldShowRetryBadge = retryCount > 0 && !isRetryTrigger;

    const handleCopyId = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(id);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    // Format duration badge text
    const durationBadgeText = durationText 
        ? (durationText.includes('after') 
            ? `+${durationText.replace(' after due time', '')}`
            : durationText.includes('before')
            ? `-${durationText.replace(' before due time', '')}`
            : durationText.includes('at due time')
            ? 'Due time'
            : durationText)
        : null;

    // Only highlight duplicate sends (errors), not successful sends
    const isDuplicateSend = sendInfo?.isDuplicate;

    return (
        <div className={cn(
            "relative flex gap-4 pb-6",
            isDuplicateSend && "bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 pl-2 -ml-2 rounded"
        )}>
            {/* Timeline line */}
            {!isLast && (
                <div className={cn(
                    "absolute left-[11px] top-8 bottom-0 w-0.5",
                    isDuplicateSend ? "bg-red-300" : "bg-slate-200 dark:bg-slate-700"
                )} style={isDuplicateSend ? { left: '19px' } : {}} />
            )}

            {/* Timeline dot - larger and colored only for duplicate sends (errors) */}
            <div className={cn(
                "relative z-10 flex shrink-0 items-center justify-center rounded-full border-2",
                isDuplicateSend
                    ? "h-8 w-8 bg-red-100 dark:bg-red-800 border-red-500"
                    : "h-6 w-6 bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600"
            )}>
                {isDuplicateSend ? (
                    <Send className="h-4 w-4 text-red-600 dark:text-red-400" />
                ) : (
                    <div className="h-2 w-2 rounded-full bg-slate-400 dark:bg-slate-500" />
                )}
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-2">
                    {/* Left: Log message and badges */}
                    <div className="flex-1 min-w-0">
                        {/* Primary: Log message with duplicate send warning badge only */}
                        {log.metadata?.logMessage && (
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                {sendInfo?.isDuplicate && (
                                    <Badge
                                        className="text-sm font-bold px-3 py-1 bg-red-600 hover:bg-red-700 text-white"
                                    >
                                        <Send className="h-4 w-4 mr-1.5" />
                                        SEND #{sendInfo.sendNumber}
                                        <AlertTriangle className="h-4 w-4 ml-1.5" />
                                        <span className="ml-1">DUPLICATE!</span>
                                    </Badge>
                                )}
                                <span className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                    {log.metadata.logMessage}
                                </span>
                            </div>
                        )}
                        
                        {/* Compact row: Status + Retry count + WhatsApp status + Timing badges + Error icon */}
                        <div className="flex items-center gap-2 flex-wrap">
                            {/* Status badge */}
                            <MessageLogStatusBadge
                                status={log.status}
                                ack={log.ack}
                                isGroup={chatKindOf(log.message?.contactList?.[0]?.id ?? log.contactWAId) === 'group'}
                            />
                            
                            {/* Retry count (if retry) - but not for retry trigger logs */}
                            {shouldShowRetryBadge && (
                                <Badge variant="outline" className="text-xs px-2 py-0.5 bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300 border-orange-300 dark:border-orange-700">
                                    Retry #{retryCount}
                                </Badge>
                            )}
                            
                            {/* WhatsApp status */}
                            {log.metadata?.whatsappStatus && (
                                <WhatsAppStatusBadge status={log.metadata.whatsappStatus} waVersion={log.metadata.waVersion} />
                            )}
                            
                            {/* Granular Status Icons */}
                            <GranularStatusIcons metadata={log.metadata} />
                            
                            {/* Timing badges */}
                            {durationBadgeText && (
                                <Badge 
                                    variant="outline" 
                                    className="text-xs font-normal bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 px-2 py-0.5"
                                >
                                    {durationBadgeText}
                                </Badge>
                            )}
                            {deltaText && (
                                <Badge 
                                    variant="outline" 
                                    className="text-xs font-normal bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 px-2 py-0.5"
                                >
                                    {deltaText}
                                </Badge>
                            )}
                            
                                {/* Error icon (if error exists) */}
                                {hasError && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="cursor-help">
                                                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent 
                                            className="max-w-md"
                                            side="top"
                                        >
                                            <p className="text-xs break-words">
                                                {errorMessage && errorMessage.length > 100 
                                                    ? `${errorMessage.substring(0, 100)}...` 
                                                    : errorMessage}
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                        </div>
                    </div>
                    
                    {/* Right: Timestamp */}
                    <div className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        {moment(log.createdAt).format('MM/DD/YYYY, h:mm:ss A')}
                    </div>
                </div>

                {/* Expandable Details Panel - Only show if there's additional data */}
                {(log.waMessageKey || hasError || log.ack !== undefined || log.mqttClientId || log.clientType || log.metadata?.debugInfo || log.metadata?.waVersion || log.metadata?.waMeUser || log.metadata?.waState) && (
                    <Collapsible open={isDetailsExpanded} onOpenChange={setIsDetailsExpanded}>
                        <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100">
                                {isDetailsExpanded ? (
                                    <>
                                        <ChevronUp className="h-3 w-3 mr-1" />
                                        Hide Details
                                    </>
                                ) : (
                                    <>
                                        <ChevronDown className="h-3 w-3 mr-1" />
                                        Show Details
                                    </>
                                )}
                            </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2 space-y-2">
                            <Separator className="bg-slate-200 dark:bg-slate-700" />
                            
                            {/* Message Key (if exists) */}
                            {log.waMessageKey && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-600 dark:text-slate-300 font-medium">Message Key:</span>
                                    <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200 break-all">
                                        {log.waMessageKey}
                                    </code>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => handleCopyId(e, log.waMessageKey!)}
                                    >
                                        <Copy className={copiedId === log.waMessageKey ? "h-3 w-3 text-green-600 dark:text-green-400" : "h-3 w-3 text-slate-600 dark:text-slate-400"} />
                                    </Button>
                                </div>
                            )}

                            {/* Status (if exists) */}
                            {log.message?.status && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-600 dark:text-slate-300 font-medium">Status:</span>
                                    <Badge 
                                        variant="outline" 
                                        className="text-xs bg-purple-50 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700 px-2 py-0.5"
                                    >
                                        {log.message.status}
                                    </Badge>
                                </div>
                            )}


                            {/* Message ID (if exists) */}
                            {log.messageId && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-600 dark:text-slate-300 font-medium">Message ID:</span>
                                    <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200 break-all">
                                        {log.messageId}
                                    </code>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => handleCopyId(e, log.messageId!)}
                                    >
                                        <Copy className={copiedId === log.messageId ? "h-3 w-3 text-green-600 dark:text-green-400" : "h-3 w-3 text-slate-600 dark:text-slate-400"} />
                                    </Button>
                                </div>
                            )}

                            {/* Client ID (if exists) */}
                            {log.mqttClientId && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-600 dark:text-slate-300 font-medium">Client ID:</span>
                                    <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200 break-all">
                                        {log.mqttClientId}
                                    </code>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => handleCopyId(e, log.mqttClientId!)}
                                    >
                                        <Copy className={copiedId === log.mqttClientId ? "h-3 w-3 text-green-600 dark:text-green-400" : "h-3 w-3 text-slate-600 dark:text-slate-400"} />
                                    </Button>
                                </div>
                            )}

                            {/* Client Type (if exists) */}
                            {log.clientType && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-600 dark:text-slate-300 font-medium">Client Type:</span>
                                    <Badge 
                                        variant="outline" 
                                        className="text-xs bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 px-2 py-0.5"
                                    >
                                        {log.clientType}
                                    </Badge>
                                </div>
                            )}

                            {/* WhatsApp Version (if exists) */}
                            {log.metadata?.waVersion && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-600 dark:text-slate-300 font-medium">WhatsApp Version:</span>
                                    <Badge 
                                        variant="outline" 
                                        className="text-xs bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700 px-2 py-0.5"
                                    >
                                        v{log.metadata.waVersion}
                                    </Badge>
                                </div>
                            )}

                            {/* Me User (if exists) */}
                            {log.metadata?.waMeUser && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-600 dark:text-slate-300 font-medium">Me User:</span>
                                    <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200 break-all">
                                        {log.metadata.waMeUser}
                                    </code>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => handleCopyId(e, log.metadata!.waMeUser!)}
                                    >
                                        <Copy className={copiedId === log.metadata!.waMeUser ? "h-3 w-3 text-green-600 dark:text-green-400" : "h-3 w-3 text-slate-600 dark:text-slate-400"} />
                                    </Button>
                                </div>
                            )}
                            
                            {/* WhatsApp State (if exists) */}
                            {log.metadata?.waState && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-600 dark:text-slate-300 font-medium">WhatsApp State:</span>
                                    <Badge 
                                        variant="outline" 
                                        className={cn("text-xs px-2 py-0.5 whitespace-nowrap", 
                                            log.metadata.waState === 'CONNECTED' 
                                            ? 'bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700' 
                                            : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600'
                                        )}
                                    >
                                        {log.metadata.waState}
                                    </Badge>
                                </div>
                            )}

                             {/* WhatsApp Stream (if exists) */}
                             {log.metadata?.waStream && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-600 dark:text-slate-300 font-medium">WhatsApp Stream:</span>
                                    <Badge 
                                        variant="outline" 
                                        className="text-xs bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 px-2 py-0.5"
                                    >
                                        {log.metadata.waStream}
                                    </Badge>
                                </div>
                            )}

                             {/* WhatsApp Has Synced (if exists) */}
                             {log.metadata?.waHasSynced !== undefined && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-600 dark:text-slate-300 font-medium">Has Synced:</span>
                                    <Badge 
                                        variant="outline" 
                                        className={cn("text-xs px-2 py-0.5 whitespace-nowrap", 
                                            log.metadata.waHasSynced
                                            ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700' 
                                            : 'bg-yellow-50 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700'
                                        )}
                                    >
                                        {log.metadata.waHasSynced ? 'True' : 'False'}
                                    </Badge>
                                </div>
                            )}

                            {/* Full error message (if error exists) */}
                            {hasError && errorMessage && (
                                <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
                                    <div className="flex items-start gap-2">
                                        <strong>Error:</strong>
                                        <span className="flex-1 break-words">
                                            {errorMessage}
                                        </span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0 shrink-0"
                                            onClick={(e) => handleCopyId(e, errorMessage)}
                                        >
                                            <Copy className={copiedId === errorMessage ? "h-3 w-3 text-green-600 dark:text-green-400" : "h-3 w-3 text-slate-600 dark:text-slate-400"} />
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* ACK info (if applicable) */}
                            {log.ack !== undefined && (
                                <div className="text-xs text-slate-600 dark:text-slate-300">
                                    <span className="font-medium">ACK:</span> {log.ack} {log.ackData ? `(${JSON.stringify(log.ackData)})` : ''}
                                </div>
                            )}

                            {/* Debug Info (if exists) */}
                            {log.metadata?.debugInfo && (
                                <div className="text-sm bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded p-2">
                                    <div className="font-medium text-blue-900 dark:text-blue-200 mb-2">Debug Info</div>
                                    {log.metadata.debugInfo.totalMessagesFetched !== undefined && (
                                        <div className="text-xs text-blue-800 dark:text-blue-300 mb-1">
                                            <span className="font-medium">Messages Checked:</span> {log.metadata.debugInfo.totalMessagesFetched}
                                        </div>
                                    )}
                                    {log.metadata.debugInfo.top5Messages && log.metadata.debugInfo.top5Messages.length > 0 && (
                                        <div className="mt-2">
                                            <div className="text-xs font-medium text-blue-900 dark:text-blue-200 mb-1">Top {log.metadata.debugInfo.top5Messages.length} Messages:</div>
                                            <div className="space-y-1">
                                                {log.metadata.debugInfo.top5Messages.map((msg, idx) => (
                                                    <div key={idx} className="text-xs bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-700 rounded p-1.5">
                                                        <div className="font-mono text-[10px] text-blue-700 dark:text-blue-300 mb-0.5">ID: {msg.id}</div>
                                                        {msg.body && (
                                                            <div className="text-slate-700 dark:text-slate-300 truncate">
                                                                <span className="font-medium">Body:</span> {msg.body}
                                                            </div>
                                                        )}
                                                        {msg.caption && (
                                                            <div className="text-slate-700 dark:text-slate-300 truncate">
                                                                <span className="font-medium">Caption:</span> {msg.caption}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </CollapsibleContent>
                    </Collapsible>
                )}
            </div>
        </div>
    );
}

